import type { ReactElement } from 'react'
import { camelCase } from 'lodash-es'
import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router'

import { DocumentMeta } from '../components/DocumentMeta'
import { confirmEmailChange, getCustomerDetails, logoutCustomer } from '../store/actions/customer'
import { generateCustomerAccountUrl, generateShopUrl } from '../urlGenerators'
import { useTranslate } from '../utils/translate'
import { withQueryAndScope } from '../urlGeneratorsPlain'
import Theme from '../components/Theme'
import usePrevious from '../utils/hooks/usePrevious'

function Account(): ReactElement {
  const { section } = useParams()

  const dispatch = useDispatch<GlobalDispatch>()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const shopTitle = useSelector<State, string>((state) => state.getIn(['shop', 'title']))
  const t = useTranslate('shop', 'views.storefrontView.customerAccountSection')

  const redirectToLogin = useCallback(() => {
    const path = encodeURIComponent([generateCustomerAccountUrl(), section].filter(Boolean).join('/'))
    navigate(withQueryAndScope(`${generateShopUrl()}?login=1&redirect=${path}`, location))
  }, [location, section, navigate])

  useEffect(function handleConfirmEmailChange() {
    if (location.pathname.endsWith('confirm-email-change') && typeof searchParams.get('tkn') === 'string') {
      ;(async () => {
        const tkn = searchParams.get('tkn')
        if (typeof tkn !== 'string') return // shut up TypeScript

        try {
          await dispatch(confirmEmailChange(tkn))
          await dispatch(logoutCustomer())
          navigate(withQueryAndScope(generateCustomerAccountUrl(), location), { replace: true })
        } catch {
          if (!isLoggedIn) redirectToLogin()
        }
      })()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const isLoggedIn = useSelector<State, boolean>((state) => state.getIn(['customer', 'loggedIn']))
  const previousLocationKey = usePrevious(location.key)
  useEffect(
    function redirectToLoginWhenNeeded() {
      if (!isLoggedIn && location.key !== previousLocationKey && location.pathname.startsWith('/customer-account')) {
        redirectToLogin()
      }
    },
    [isLoggedIn, section, location, previousLocationKey, redirectToLogin],
  )

  const viewError = useSelector<State, ImmutableMap>((state) => state.getIn(['view', 'error']))
  useEffect(() => {
    if (viewError) dispatch(logoutCustomer())
  }, [dispatch, viewError])

  let tabTitle = t('profileTab.title')
  if (section && section !== 'confirm-email-change') {
    tabTitle = t(`${camelCase(section)}Tab.title`)
  }

  return viewError ? (
    <Theme withLayout error={viewError} currentView="Account" />
  ) : (
    <>
      <DocumentMeta title={`${tabTitle} - ${shopTitle}`} meta={[{ name: 'robots', content: 'noindex, follow' }]} />
      <Theme withLayout currentView="Account">
        {(renderView, props) => renderView('Account', { ...props, section })}
      </Theme>
    </>
  )
}

Account.storeUpdate = (props: RouteComponentProps, state: State) => {
  const isForwardNavigation = props.location.action === 'PUSH'
  const updates: [ReturnType<typeof getCustomerDetails>?] = []

  if ((isForwardNavigation || !state.getIn(['customer', 'billingAddress'])) && state.getIn(['customer', 'loggedIn'])) {
    updates.push(getCustomerDetails())
  }

  return updates
}

Account.contentCreationDisabled = () => true

export default Account
